/**
 * Performance Monitor Utility
 *
 * This utility tracks and reports key performance metrics for page loads
 * and user interactions to help identify bottlenecks and improve
 * the overall application performance.
 */
import { API_BASE_URL } from "../../src/apiConstants";

// Store performance metrics by category
const metrics = {
  pageLoads: {},
  interactions: {},
  resources: {},
  longTasks: [],
};

// Configuration options
const config = {
  enabled: true,
  sampleRate: 1.0, // 100% of users by default
  sendBeacon: true,
  apiEndpoint: `${API_BASE_URL}/performance/frontend`,
  debugMode: import.meta.env.MODE === "development",
};

let isInitialized = false;
let lifecycleVersion = 0;
let monitorApi = null;
const observers = new Set();
const eventListeners = [];
const timeouts = new Set();

const isLifecycleActive = (version) =>
  isInitialized && lifecycleVersion === version;

const addOwnedEventListener = (target, type, listener, options) => {
  target.addEventListener(type, listener, options);
  eventListeners.push({ target, type, listener, options });
};

const scheduleTimeout = (callback, delay) => {
  const version = lifecycleVersion;
  const timeoutId = setTimeout(() => {
    timeouts.delete(timeoutId);
    if (isLifecycleActive(version)) {
      callback();
    }
  }, delay);

  timeouts.add(timeoutId);
  return timeoutId;
};

/**
 * Initialize the performance monitoring system
 * @param {Object} options - Configuration options
 */
export const initPerformanceMonitor = (options = {}) => {
  Object.assign(config, options);

  if (isInitialized) return cleanupPerformanceMonitor;
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !config.enabled ||
    Math.random() > config.sampleRate
  ) {
    return cleanupPerformanceMonitor;
  }

  lifecycleVersion += 1;
  isInitialized = true;
  const version = lifecycleVersion;

  monitorApi = {
    metrics,
    trackPageLoad,
    trackInteraction,
    markStart,
    markEnd,
    measure,
    getLCP,
    getFID,
    getCLS,
    getTTFB,
    getResourceMetrics,
    printReport,
    config,
  };
  window.__NEXUS_PERFORMANCE_MONITOR__ = monitorApi;

  trackPageLoad();
  registerPerformanceObservers(version);
  registerEventListeners(version);

  return cleanupPerformanceMonitor;
};
 
export const cleanupPerformanceMonitor = () => {
  isInitialized = false;
  lifecycleVersion += 1;

  observers.forEach((observer) => observer.disconnect());
  observers.clear();

  eventListeners.forEach(({ target, type, listener, options }) =>
    target.removeEventListener(type, listener, options),
  );
  eventListeners.length = 0;

  timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  timeouts.clear();

  if (
    typeof window !== "undefined" &&
    window.__NEXUS_PERFORMANCE_MONITOR__ === monitorApi
  ) {
    delete window.__NEXUS_PERFORMANCE_MONITOR__;
  }
  monitorApi = null;
};

/**
 * Set up performance observers to track metrics
 * Uses Modern Web Performance APIs
 */
const registerPerformanceObservers = (version) => {
  if (typeof window.PerformanceObserver !== "function") return;

  const observe = (entryTypes, callback) => {
    try {
      const observer = new window.PerformanceObserver((entries) => {
        if (isLifecycleActive(version)) {
          callback(entries);
        }
      });
      observer.observe({ entryTypes });
      observers.add(observer);
    } catch (error) {
      // The browser does not support this performance entry type.
    }
  };

  observe(["longtask"], (entries) => {
    entries.getEntries().forEach((entry) => {
      metrics.longTasks.push({
        duration: entry.duration,
        startTime: entry.startTime,
        name: entry.name,
        timestamp: Date.now(),
      });

      if (config.debugMode && entry.duration > 100) {
        console.warn(`⚠️ Long task detected: ${entry.duration.toFixed(2)}ms`);
      }
    });
  });

  observe(["resource"], (entries) => {
    entries.getEntries().forEach((entry) => {
      if (
        !["script", "css", "fetch", "xmlhttprequest", "img"].includes(
          entry.initiatorType,
        )
      ) {
        return;
      }

      const url = entry.name.split("?")[0];
      const urlParts = url.split("/");
      const resourceName = urlParts[urlParts.length - 1];
      metrics.resources[resourceName] = {
        url,
        duration: entry.duration,
        size: entry.transferSize || 0,
        type: entry.initiatorType,
        timestamp: Date.now(),
      };
    });
  });

  observe(["paint"], (entries) => {
    entries.getEntries().forEach((entry) => {
      metrics.pageLoads[entry.name] = entry.startTime;
    });
  });

  observe(["navigation"], (entries) => {
    const navEntry = entries.getEntries()[0];
    if (!navEntry) return;

    metrics.pageLoads.navigationTiming = {
      domComplete: navEntry.domComplete,
      domInteractive: navEntry.domInteractive,
      loadEventEnd: navEntry.loadEventEnd,
      responseEnd: navEntry.responseEnd,
      ttfb: navEntry.responseStart - navEntry.requestStart,
      timestamp: Date.now(),
    };
  });

  observe(["largest-contentful-paint"], (entries) => {
    const lcpEntry = entries.getEntries().pop();
    if (lcpEntry) {
      metrics.pageLoads.lcp = lcpEntry.startTime;
    }
  });

  observe(["first-input"], (entries) => {
    entries.getEntries().forEach((entry) => {
      metrics.pageLoads.fid = entry.processingStart - entry.startTime;
    });
  });

  observe(["layout-shift"], (entries) => {
    let cumulativeLayoutShift = 0;
    entries.getEntries().forEach((entry) => {
      if (!entry.hadRecentInput) {
        cumulativeLayoutShift += entry.value;
      }
    });
    metrics.pageLoads.cls = cumulativeLayoutShift;
  });
};

/**
 * Register event listeners for user interactions
 */
const registerEventListeners = (version) => {
  const handleVisibilityChange = () => {
    if (
      isLifecycleActive(version) &&
      document.visibilityState === "visible"
    ) {
      markStart("visibility-change");
    }
  };
  const handleBeforeUnload = () => {
    if (isLifecycleActive(version)) {
      sendMetrics();
    }
  };

  addOwnedEventListener(
    document,
    "visibilitychange",
    handleVisibilityChange,
  );
  addOwnedEventListener(window, "beforeunload", handleBeforeUnload);
};

/**
 * Track a page load event
 * @param {string} pagePath - Path of the page being loaded
 */
export const trackPageLoad = (pagePath) => {
  if (!isInitialized || typeof window === "undefined") return;

  const timestamp = Date.now();
  const normalizedPath = String(
    pagePath ?? window.location.pathname,
  ).split("?")[0];

  metrics.pageLoads[normalizedPath] = {
    timestamp,
    measurements: {},
  };

  markStart(`page-start-${normalizedPath}`);
  scheduleTimeout(() => {
    markEnd(`page-end-${normalizedPath}`);
    measure(
      `page-load-${normalizedPath}`,
      `page-start-${normalizedPath}`,
      `page-end-${normalizedPath}`,
    );

    const loadMetrics = {
      lcp: getLCP(),
      fid: getFID(),
      cls: getCLS(),
      ttfb: getTTFB(),
      domComplete: getDOMComplete(),
      timestamp,
    };

    const pageMetrics = metrics.pageLoads[normalizedPath];
    if (pageMetrics && typeof pageMetrics === "object") {
      pageMetrics.measurements = loadMetrics;
    }

    sendMetrics({
      type: "pageLoad",
      path: normalizedPath,
      metrics: loadMetrics,
    });
  }, 3000);
};

/**
 * Track a user interaction
 * @param {string} interactionType - The type of interaction (e.g., 'click', 'form-submit')
 * @param {string} elementId - Identifier for the element being interacted with
 * @param {Object} additionalData - Any additional data to record
 */
export const trackInteraction = (
  interactionType,
  elementId,
  additionalData = {},
) => {
  if (!isInitialized) return () => undefined;

  const version = lifecycleVersion;
  const timestamp = Date.now();
  const interactionKey = `${interactionType}-${elementId}-${timestamp}`;
  const measurementKey = `interaction-${interactionKey}`;

  metrics.interactions[interactionKey] = {
    type: interactionType,
    elementId,
    timestamp,
    ...additionalData,
  };

  markStart(measurementKey);

  return () => {
    if (!isLifecycleActive(version)) return undefined;

    markEnd(measurementKey);
    measure(measurementKey, measurementKey, measurementKey);

    const duration = getLastMeasurementDuration();
    metrics.interactions[interactionKey].duration = duration;

    if (config.debugMode && duration > 100) {
      console.log(
        `⏱️ Slow Interaction [${interactionType}] on ${elementId}: ${duration.toFixed(2)}ms`,
      );
    }

    sendMetrics({
      type: "interaction",
      interactionType,
      elementId,
      duration,
      timestamp,
    });

    return duration;
  };
};

/**
 * Mark the start of a performance measurement
 * @param {string} markName - The name of the mark
 */
export const markStart = (markName) => {
  try {
    performance.mark(`${markName}-start`);
  } catch (e) {}
};

/**
 * Mark the end of a performance measurement
 * @param {string} markName - The name of the mark
 */
export const markEnd = (markName) => {
  try {
    performance.mark(`${markName}-end`);
  } catch (e) {}
};

/**
 * Create a performance measurement between marks
 * @param {string} measureName - The name of the measurement
 * @param {string} startMark - The name of the start mark
 * @param {string} endMark - The name of the end mark
 */
export const measure = (measureName, startMark, endMark) => {
  try {
    if (
      performance.getEntriesByName(`${startMark}-start`).length > 0 &&
      performance.getEntriesByName(`${endMark}-end`).length > 0
    ) {
      performance.measure(measureName, `${startMark}-start`, `${endMark}-end`);
    } else {
    }
  } catch (e) {}
};

/**
 * Get the duration of the last measurement
 * @returns {number} The duration in milliseconds
 */
const getLastMeasurementDuration = () => {
  try {
    const entries = performance.getEntriesByType("measure");
    if (entries.length > 0) {
      return entries[entries.length - 1].duration;
    }
  } catch (e) {}
  return 0;
};

/**
 * Get Largest Contentful Paint metric
 * @returns {number} LCP in milliseconds
 */
export const getLCP = () => {
  return metrics.pageLoads.lcp || 0;
};

/**
 * Get First Input Delay metric
 * @returns {number} FID in milliseconds
 */
export const getFID = () => {
  return metrics.pageLoads.fid || 0;
};

/**
 * Get Cumulative Layout Shift score
 * @returns {number} CLS score (unitless)
 */
export const getCLS = () => {
  return metrics.pageLoads.cls || 0;
};

/**
 * Get Time to First Byte metric
 * @returns {number} TTFB in milliseconds
 */
export const getTTFB = () => {
  try {
    if (metrics.pageLoads.navigationTiming) {
      return metrics.pageLoads.navigationTiming.ttfb;
    }

    // Fallback calculation
    const navigationEntries = performance.getEntriesByType("navigation");
    if (navigationEntries.length > 0) {
      const navTiming = navigationEntries[0];
      return navTiming.responseStart - navTiming.requestStart;
    }
  } catch (e) {}
  return 0;
};

/**
 * Get DOM Complete time
 * @returns {number} DOM Complete time in milliseconds
 */
export const getDOMComplete = () => {
  try {
    if (metrics.pageLoads.navigationTiming) {
      return metrics.pageLoads.navigationTiming.domComplete;
    }

    // Fallback calculation
    const navigationEntries = performance.getEntriesByType("navigation");
    if (navigationEntries.length > 0) {
      return navigationEntries[0].domComplete;
    }
  } catch (e) {}
  return 0;
};

/**
 * Get resource load metrics
 * @returns {Object} Object containing resource metrics
 */
export const getResourceMetrics = () => {
  return metrics.resources;
};

/**
 * Send metrics to the server
 * @param {Object} data - Specific metrics to send
 */
const sendMetrics = (data = null) => {
  // Only send if API endpoint is configured
  if (!config.apiEndpoint) return;

  try {
    const metricsToSend = data || {
      pageLoads: metrics.pageLoads,
      interactions: Object.values(metrics.interactions).slice(-10), // Only send recent interactions
      longTasks: metrics.longTasks.filter((task) => task.duration > 100), // Only send significant long tasks
    };

    // Add device and environment info
    const metricPayload = {
      ...metricsToSend,
      device: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        connection: navigator.connection
          ? {
              effectiveType: navigator.connection.effectiveType,
              rtt: navigator.connection.rtt,
              downlink: navigator.connection.downlink,
            }
          : null,
        timestamp: Date.now(),
      },
    };

    // Use Beacon API if available and enabled for unload events
    if (
      config.sendBeacon &&
      navigator.sendBeacon &&
      window.document.visibilityState !== "visible"
    ) {
      // this request has been running non stop preventing other requests so temporarily commenting it out
      // navigator.sendBeacon(
      //   config.apiEndpoint,
      //   JSON.stringify(metricPayload)
      // );
      return;
    }

    // Otherwise use fetch

    // this request has been running non stop preventing other requests so temporarily commenting it out

    // fetch(`${API_BASE_URL}/performance/frontend`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json'
    //   },
    //   body: JSON.stringify(metricPayload),
    //   // Use keepalive to ensure the request completes even if the page is unloaded
    //   keepalive: true
    // }).catch(error => {
    //   if (config.debugMode) {
    //           }
    // });
  } catch (error) {
    if (config.debugMode) {
    }
  }
};

/**
 * Print a performance report to the console
 */

export const printReport = () => {
  console.group("📊 NexusDialer Performance Report");

  // Web Vitals
  console.log("Core Web Vitals:");
  console.table({
    "LCP (Largest Contentful Paint)": `${getLCP().toFixed(2)}ms`,
    "FID (First Input Delay)": `${getFID().toFixed(2)}ms`,
    "CLS (Cumulative Layout Shift)": getCLS().toFixed(3),
    "TTFB (Time To First Byte)": `${getTTFB().toFixed(2)}ms`,
  });

  // Page Load Times for recent pages
  console.log("Recent Page Loads:");
  const pageLoadEntries = Object.entries(metrics.pageLoads)
    .filter(([key, value]) => typeof value === "object" && value.timestamp)
    .sort((a, b) => b[1].timestamp - a[1].timestamp)
    .slice(0, 5);

  console.table(
    pageLoadEntries.map(([page, data]) => ({
      Page: page,
      "Load Time": data.measurements?.domComplete
        ? `${data.measurements.domComplete.toFixed(2)}ms`
        : "N/A",
      LCP: data.measurements?.lcp
        ? `${data.measurements.lcp.toFixed(2)}ms`
        : "N/A",
      Timestamp: new Date(data.timestamp).toLocaleTimeString(),
    })),
  );

  // Recent Interactions
  console.log("Recent Interactions:");
  const interactionEntries = Object.entries(metrics.interactions)
    .sort(([, a], [, b]) => b.timestamp - a.timestamp)
    .slice(0, 10);

  console.table(
    interactionEntries.map(([key, data]) => ({
      Type: data.type,
      Element: data.elementId,
      Duration: data.duration ? `${data.duration.toFixed(2)}ms` : "N/A",
      Timestamp: new Date(data.timestamp).toLocaleTimeString(),
    })),
  );

  // Long Tasks
  if (metrics.longTasks.length > 0) {
    console.log("Long Tasks (>100ms):");
    console.table(
      metrics.longTasks.map((task) => ({
        Duration: `${task.duration.toFixed(2)}ms`,
        Timestamp: new Date(task.timestamp).toLocaleTimeString(),
      })),
    );
  }

  // Slow Resources
  const slowResources = Object.entries(metrics.resources)
    .filter(([, data]) => data.duration > 500)
    .sort(([, a], [, b]) => b.duration - a.duration)
    .slice(0, 10);

  if (slowResources.length > 0) {
    console.log("Slow Resources (>500ms):");
    console.table(
      slowResources.map(([name, data]) => ({
        Resource: name,
        Type: data.type,
        Duration: `${data.duration.toFixed(2)}ms`,
        Size: `${(data.size / 1024).toFixed(2)} KB`,
      })),
    );
  }

  console.groupEnd();
};

/**
 * Create React Hook for measuring component performance
 * @param {string} componentName - The name of the component to measure
 * @returns {Object} Object containing tracking functions
 */
export const useComponentPerformance = (componentName) => {
  return {
    trackRender: () => {
      markStart(`render-${componentName}`);
      return () => {
        markEnd(`render-${componentName}`);
        measure(
          `render-${componentName}`,
          `render-start-${componentName}`,
          `render-end-${componentName}`,
        );

        const duration = getLastMeasurementDuration();
        if (config.debugMode && duration > 50) {
          console.log(
            `🔍 Slow Render: ${componentName} took ${duration.toFixed(2)}ms`,
          );
        }
        return duration;
      };
    },
    trackEffect: (effectName) => {
      const effectKey = `effect-${componentName}-${effectName}`;
      markStart(effectKey);
      return () => {
        markEnd(effectKey);
        measure(effectKey, `${effectKey}-start`, `${effectKey}-end`);

        const duration = getLastMeasurementDuration();
        if (config.debugMode && duration > 50) {
          console.log(
            `🔍 Slow Effect: ${effectName} in ${componentName} took ${duration.toFixed(2)}ms`,
          );
        }
        return duration;
      };
    },
    trackCallback: (callbackName) => {
      return () => {
        const callbackKey = `callback-${componentName}-${callbackName}-${Date.now()}`;
        markStart(callbackKey);
        return () => {
          markEnd(callbackKey);
          measure(callbackKey, `${callbackKey}-start`, `${callbackKey}-end`);

          const duration = getLastMeasurementDuration();
          if (config.debugMode && duration > 50) {
            console.log(
              `🔍 Slow Callback: ${callbackName} in ${componentName} took ${duration.toFixed(2)}ms`,
            );
          }
          return duration;
        };
      };
    },
  };
};

/**
 * Create a performance monitoring button for developers
 */
export const createPerformanceMonitorButton = () => {
  if (import.meta.env.MODE !== "development") {
    return;
  }

  const button = document.createElement("button");
  button.innerText = "📊 Performance";
  button.style.position = "fixed";
  button.style.bottom = "10px";
  button.style.left = "120px"; // Position next to bundle button
  button.style.zIndex = "9999";
  button.style.background = "#3182ce";
  button.style.color = "white";
  button.style.border = "none";
  button.style.borderRadius = "4px";
  button.style.padding = "8px 12px";
  button.style.fontFamily = "monospace";
  button.style.cursor = "pointer";

  button.addEventListener("click", () => {
    printReport();
  });

  document.body.appendChild(button);
};

export default {
  initPerformanceMonitor,
  cleanupPerformanceMonitor,
  trackPageLoad,
  trackInteraction,
  markStart,
  markEnd,
  measure,
  useComponentPerformance,
  createPerformanceMonitorButton,
};
